import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@examsreactor/ui';

type Period = 'monthly' | 'annual';

interface Plan {
  code: string;
  name: string;
  description: string;
  monthlyUsd: number;
  annualUsd: number;
  highlighted?: boolean;
  features: { label: string; included: boolean }[];
}

const plans: Plan[] = [
  {
    code: 'free',
    name: 'Free',
    description: 'Empieza gratis. Sin tarjeta.',
    monthlyUsd: 0,
    annualUsd: 0,
    features: [
      { label: '20 preguntas rotativas por certificacion', included: true },
      { label: 'Reporte basico por dominio', included: true },
      { label: 'Modo examen acotado', included: true },
      { label: 'Banco completo de preguntas', included: false },
      { label: 'Reportes premium con heatmap', included: false },
      { label: 'Plan de estudio personalizado', included: false },
      { label: 'PDF descargable', included: false },
      { label: 'Historial completo', included: false },
    ],
  },
  {
    code: 'azure_pass',
    name: 'Azure Pass',
    description: 'Banco completo Azure',
    monthlyUsd: 12.9,
    annualUsd: 129,
    features: [
      { label: '500+ preguntas por cert Azure', included: true },
      { label: 'Reportes premium con heatmap', included: true },
      { label: 'Plan de estudio 3-5 acciones', included: true },
      { label: 'Modo examen + practica + por temas', included: true },
      { label: 'PDF descargable', included: true },
      { label: 'Historial y tendencia', included: true },
      { label: 'Comparacion vs cohorte', included: true },
      { label: 'Soporte <24h habiles', included: true },
    ],
  },
  {
    code: 'all_access',
    name: 'All Access',
    description: 'Azure + AWS + futuros vendors',
    monthlyUsd: 19.9,
    annualUsd: 199,
    highlighted: true,
    features: [
      { label: 'Banco completo Azure y AWS', included: true },
      { label: 'Reportes premium con heatmap', included: true },
      { label: 'Plan de estudio 3-5 acciones', included: true },
      { label: 'Todos los modos de simulador', included: true },
      { label: 'PDF descargable', included: true },
      { label: 'Historial y tendencia', included: true },
      { label: 'Comparacion vs cohorte', included: true },
      { label: 'Acceso anticipado a beta features', included: true },
    ],
  },
  {
    code: 'aws_pass',
    name: 'AWS Pass',
    description: 'Banco completo AWS',
    monthlyUsd: 12.9,
    annualUsd: 129,
    features: [
      { label: '500+ preguntas por cert AWS', included: true },
      { label: 'Reportes premium con heatmap', included: true },
      { label: 'Plan de estudio 3-5 acciones', included: true },
      { label: 'Modo examen + practica + por temas', included: true },
      { label: 'PDF descargable', included: true },
      { label: 'Historial y tendencia', included: true },
      { label: 'Comparacion vs cohorte', included: true },
      { label: 'Soporte <24h habiles', included: true },
    ],
  },
];

export function Pricing() {
  const [period, setPeriod] = useState<Period>('monthly');

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Planes simples</h1>
        <p className="mt-4 text-lg text-text-secondary">
          Sin contrato. Sin tarjeta para el plan free. Cancela cuando quieras.
        </p>
      </div>

      <div className="mt-10 flex justify-center">
        <div className="inline-flex rounded-lg border border-border-subtle bg-bg-secondary p-1">
          <button
            type="button"
            onClick={() => setPeriod('monthly')}
            className={cn(
              'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
              period === 'monthly' ? 'bg-bg-primary text-text-primary shadow-sm' : 'text-text-secondary',
            )}
          >
            Mensual
          </button>
          <button
            type="button"
            onClick={() => setPeriod('annual')}
            className={cn(
              'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
              period === 'annual' ? 'bg-bg-primary text-text-primary shadow-sm' : 'text-text-secondary',
            )}
          >
            Anual <span className="ml-1 text-xs font-semibold text-accent-success">17% off</span>
          </button>
        </div>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-4">
        {plans.map((p) => {
          const price = period === 'monthly' ? p.monthlyUsd : p.annualUsd;
          const isFree = p.code === 'free';
          return (
            <div
              key={p.code}
              className={cn(
                'card flex flex-col',
                p.highlighted && 'border-accent-primary ring-1 ring-accent-primary',
              )}
            >
              {p.highlighted && (
                <span className="mb-3 inline-flex w-fit items-center rounded-full bg-accent-primary/10 px-2.5 py-0.5 text-xs font-semibold text-accent-primary">
                  Mas popular
                </span>
              )}
              <h3 className="text-xl font-bold">{p.name}</h3>
              <p className="mt-1 text-sm text-text-secondary">{p.description}</p>
              <div className="mt-5">
                {isFree ? (
                  <div className="text-3xl font-bold">Gratis</div>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">USD {price}</span>
                    <span className="text-sm text-text-tertiary">/{period === 'monthly' ? 'mes' : 'ano'}</span>
                  </div>
                )}
              </div>
              <Link
                to={isFree ? '/registro' : `/registro?plan=${p.code}&period=${period}`}
                className={cn('mt-6', p.highlighted ? 'btn-primary' : 'btn-secondary', 'w-full')}
              >
                {isFree ? 'Comenzar gratis' : 'Elegir plan'}
              </Link>

              <ul className="mt-8 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f.label} className="flex items-start gap-2">
                    {f.included ? (
                      <span className="mt-0.5 text-accent-success" aria-label="Incluido">✓</span>
                    ) : (
                      <span className="mt-0.5 text-text-tertiary" aria-label="No incluido">×</span>
                    )}
                    <span className={cn(f.included ? 'text-text-primary' : 'text-text-tertiary')}>
                      {f.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-16 rounded-xl border border-border-subtle bg-bg-secondary p-6 text-center">
        <h2 className="text-lg font-semibold">Pago seguro multi-pais con Paddle</h2>
        <p className="mt-2 text-sm text-text-secondary">
          Cobertura: Peru, Chile, Brasil, Colombia, Mexico, Argentina y Espana. Charges en moneda local
          con FX automatico. Reembolso 48h sin preguntas.
        </p>
      </div>
    </div>
  );
}
